//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { BulkOrderDTO } from 'src/app/core/models/system/order/order-bulk.dto';
import { BulkOrderSummaryDTO } from 'src/app/core/models/system/order/order-bulk-summary.dto';
import { BulkOrderSummaryDataDTO } from 'src/app/core/models/system/order/order-bulk-summary-data.dto';
import { ConfigSystemParams, Duration, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from '../config-system/config-system-service';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from '../../general/exceptions-service';
import { forkJoin, interval, map, Observable, of, startWith, switchMap } from 'rxjs';
import { formatDate } from 'devextreme/localization';
import { HttpClientService } from '../../general/http-client-service';
import { Injectable } from '@angular/core';
import { IntegerResultDTO } from 'src/app/core/interfaces/general/integer-result';
import { OrderDevicesAllocatedStatsDTO } from 'src/app/core/models/system/order/order-devices-allocated-stats.dto';
import { OrderDTO } from 'src/app/core/models/system/order/order.dto';
import { OrderUsageLimitsDTO } from 'src/app/core/models/system/order/order-usage-limits.dto';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class OrdersService {
  
  constructor(
    private configSystemService: ConfigSystemService,
    private encryptionService  : EncryptionService,
    private exceptionsService  : ExceptionsService,
    private httpClientService  : HttpClientService
  ) { 
  }

  private idAndDescriptionConverter(bulkordersummary: BulkOrderSummaryDTO) {

    const getSpaces = (spaces: number): string => {
      const spaceString = '&nbsp;'.repeat(spaces);
      return spaceString;
    };

    const getDate = (date: Date | undefined): string => {
      if (date == undefined) {
        return "";
      } 

      let localDateTime = new Date(date);
      const dateString = formatDate(localDateTime,'yyyy-MM-dd HH:mm');
      return dateString;
    };

    const getOrderInfo = (orderInfo: BulkOrderSummaryDTO): string => {
      const orderStatus = ' (' + orderInfo.bulkOrderStatus + ')';
      return orderStatus;
    };

    return {id: bulkordersummary.bulkOrderGuid, description: getDate(bulkordersummary.enteredDate) + getSpaces(10) + bulkordersummary.fileName?.substring(0,50) + getOrderInfo(bulkordersummary)};
  }

  public get(userId: number): Observable<OrderDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<OrderDTO[]>(environment.eSimApi +
                                                                          eSimApiPaths.Orders +
                                                                          "?userIdEncSS=" + userIdEncSS); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getById(id: number): Observable<OrderDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi +
                                                                  eSimApiPaths.Orders + "/" +
                                                                  eSimApiPaths.OrdersGetById +
                                                                  "?idEncSS=" + idEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public deleteBulkOrder(bulkOrderGuid: string): Observable<BulkOrderDTO> {
    try {
      return this.httpClientService.delete(environment.eSimApi + eSimApiPaths.Orders + "/" + eSimApiPaths.OrdersBulkDelete+ '?bulkOrderGuid=' + bulkOrderGuid);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public deleteBulkOrderById(bulkOrderGuid: string, id: number): Observable<BulkOrderDTO> {
    try {
      return this.httpClientService.delete(environment.eSimApi + eSimApiPaths.Orders + "/" + eSimApiPaths.OrdersBulkDeleteById+ '?bulkOrderGuid=' + bulkOrderGuid + '&bulkOrderId=' + id);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getBulkOrderById(bulkOrderGuid: string, id: number): Observable<BulkOrderDTO> {
    try {
      return this.httpClientService.get<BulkOrderDTO>(environment.eSimApi + eSimApiPaths.Orders + "/" + eSimApiPaths.OrdersBulkById+ '?bulkOrderGuid=' + bulkOrderGuid + '&bulkOrderId=' + id);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getBulkOrderIdAndDescription(companyId : number): Observable<BulkOrderSummaryDataDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<BulkOrderSummaryDTO[]>(environment.eSimApi +
                                                                                     eSimApiPaths.Orders + "/" +
                                                                                     eSimApiPaths.OrdersBulkSummaries +
                                                                                     '?companyIdEncSS=' + companyIdEncSS)
                                   .pipe(
                                     map(x => x.map(this.idAndDescriptionConverter))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getOrderIdByIccid(iccid: string): Observable<IntegerResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(iccid, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const iccidEncSS=encResult.result;
                            
                            return this.httpClientService.get<IntegerResultDTO>(environment.eSimApi +
                                                                                eSimApiPaths.Orders + "/" +
                                                                                eSimApiPaths.GetOrderIdByIccid +
                                                                                "?iccidEncSS=" + iccidEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //RBW_20260915 - Updates ONLY the current-month override on the backend and preserves the SIM's base limit config (type/limit/inherit) exactly. Used by the bulk "Change This Month's Limit" instead of a full order load+save.
  public putOrderCurrentMonthLimit(orderId: number, limitCurrent: number | null, dataLimitCurrent: number | null, dataLimitCurrentDataWeightCode: string | null, updatedByUserId: number): Observable<any> {
    try {
      return this.httpClientService.put(environment.eSimApi +
                                        eSimApiPaths.Orders + "/" +
                                        eSimApiPaths.OrdersPutCurrentMonthLimit +
                                        "?id=" + orderId +
                                        (limitCurrent                   != null ? "&limitCurrent="                   + limitCurrent                   : "") +
                                        (dataLimitCurrent               != null ? "&dataLimitCurrent="               + dataLimitCurrent               : "") +
                                        (dataLimitCurrentDataWeightCode != null ? "&dataLimitCurrentDataWeightCode=" + dataLimitCurrentDataWeightCode : "") +
                                        "&updatedByUserId=" + updatedByUserId,
                                        undefined, {});
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getOrderIdByOrderNo(orderNo: string): Observable<IntegerResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(orderNo, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const orderNoEncSS=encResult.result;

                            return this.httpClientService.get<IntegerResultDTO>(environment.eSimApi +
                                                                                eSimApiPaths.Orders + "/" +
                                                                                eSimApiPaths.GetOrderIdByOrderNo +
                                                                                "?orderNoEncSS=" + orderNoEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getOrderUsageLimitsOrDefaults(id: number): Observable<OrderUsageLimitsDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const orderIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<OrderUsageLimitsDTO>(environment.eSimApi +
                                                                                   eSimApiPaths.Orders + "/" +
                                                                                   eSimApiPaths.GetOrderUsageLimitsOrDefaults +
                                                                                   "?orderIdEncSS=" + orderIdEncSS); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getOrderWithSpend(id: number): Observable<OrderDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.get<OrderDTO>(environment.eSimApi +
                                                                        eSimApiPaths.Orders + "/" +
                                                                        eSimApiPaths.GetOrderWithSpend +
                                                                        "?idEncSS=" + idEncSS); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public getOrderWithSpendUpdates(id: number): Observable<OrderDTO> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableOrderWithSpendMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getOrderWithSpend(id))
            )
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getStats(companyIdEncSS: string,
                  year          : number,
                  month         : number,
                  includeCeased : boolean=false): Observable<OrderDevicesAllocatedStatsDTO> {
    try {
      return this.httpClientService.get<OrderDevicesAllocatedStatsDTO>(
                  environment.eSimApi
                  + eSimApiPaths.Orders
                  + "/"
                  + eSimApiPaths.OrdersGetStats
                  + "?companyIdEncSS=" + companyIdEncSS
                  + "&year=" + year
                  + "&month=" + month
                  + "&day=1"
                  + (includeCeased ? "&includeCeased=true" : ""));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getOrderStatus(id: number): Observable<OrderDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.get<OrderDTO>(environment.eSimApi +
                                                                        eSimApiPaths.Orders + "/" +
                                                                        eSimApiPaths.OrdersGetStatus +
                                                                        "?idEncSS=" + idEncSS); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getBulkOrderSummary(bulkOrderGuid: string): Observable<BulkOrderSummaryDTO> {
    try {
      return this.httpClientService.get<BulkOrderSummaryDTO>(environment.eSimApi +
                                                             eSimApiPaths.Orders + "/" +
                                                             eSimApiPaths.OrdersBulkSummary +
                                                             "?bulkOrderGuid=" + bulkOrderGuid);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public validateBulkOrder(bulkOrderGuid: string): Observable<boolean> {
    try {
      return this.httpClientService.post(environment.eSimApi +
                                         eSimApiPaths.Orders + "/" +
                                         eSimApiPaths.OrdersBulkValidateOrder +
                                         "?bulkOrderGuid=" + bulkOrderGuid, "");
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public post(order: OrderDTO): Observable<OrderDTO> {
    try {
      //JSON.stringify(user)
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(order))
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEnc=encResult.result;
                            
                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.Orders +
                                                               "?objectEnc=" + objectEnc, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public postBulk(bulkOrder: BulkOrderDTO): Observable<BulkOrderDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(bulkOrder))
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEnc=encResult.result;
                            
                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.Orders + '/' +
                                                               eSimApiPaths.OrdersBulkPost +
                                                               "?objectEnc=" + objectEnc, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public put(order: OrderDTO, limitsChanged: boolean = true): Observable<OrderDTO> {
    try {
      return forkJoin({
               objectEncResult     : this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(order))
             })
             .pipe(
               switchMap(({ objectEncResult }) => {
                 const objectEnc  =objectEncResult.result;

                 return this.httpClientService.put(environment.eSimApi +
                                                   eSimApiPaths.Orders + '/' +
                                                   eSimApiPaths.OrdersPut +
                                                   "?id=" + order.id +
                                                   "&limitsChanged=" + limitsChanged,
                                                   undefined,
                                                   { data: objectEnc },
                                                   undefined);
              })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public putBulk(bulkOrder: BulkOrderDTO): Observable<BulkOrderDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(bulkOrder))
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEnc=encResult.result;
                            
                            return this.httpClientService.put(environment.eSimApi +
                                                              eSimApiPaths.Orders + '/' +
                                                              eSimApiPaths.OrdersBulkPut +
                                                              "?id=" + bulkOrder.id +
                                                              "&objectEnc=" + objectEnc, undefined, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public save(order: OrderDTO, limitsChanged: boolean = true): Observable<any> {
    try {
      //JSON.stringify(modal);
      if (order.id && <number>order.id>0)
        return this.put(order, limitsChanged);

      return this.post(order);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public saveBulk(bulkOrder: BulkOrderDTO): Observable<any> {
    try {
      if (bulkOrder.id && <number>bulkOrder.id>0)
        return this.putBulk(bulkOrder);
  
      return this.postBulk(bulkOrder);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public UploadBulkOrders(orders: BulkOrderDTO[]): Observable<BulkOrderSummaryDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(orders))
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEnc=encResult.result;
                            
                            return this.httpClientService._post<{ message: string }>(environment.eSimApi +
                                                                                     eSimApiPaths.Orders + '/' +
                                                                                     eSimApiPaths.OrdersBulkUpload, { data: objectEnc }); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
